import pacote from '../../package.json';
import { FORCE_CACHE_RESET_AT_VERSION } from '@/constants/forceCacheReset';
import { deleteAppIndexedDatabasesCompletely } from '@/talons/dbFunctions/useDBFunctions';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { getLocalStorageKeyForTable, type Tables } from '@/shared/storageFunctions';
import { readAllAppTablesForBackup, restoreAppTablesToLegacy } from '@/shared/appTablesIdbStorage';

const MARKER_KEY = 'pwa_last_force_cache_reset_version';

/** Chaves do carrinho e correlatas no storage tipado — não limpas no reset forçado. */
const CART_KEYS_TO_PRESERVE = [
  'cart_details',
  'cart_id',
  'cart_sort',
  'order_numbers',
  'client_cart',
  'cart',
] as const satisfies readonly Tables[];

function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da > db ? 1 : -1;
  }
  return 0;
}

function snapshotCartRawEntries(): [string, string][] {
  const out: [string, string][] = [];
  for (const name of CART_KEYS_TO_PRESERVE) {
    const key = getLocalStorageKeyForTable(name);
    const value = localStorage.getItem(key);
    if (value !== null) out.push([key, value]);
  }
  return out;
}

function restoreRawEntries(entries: [string, string][]) {
  for (const [k, v] of entries) localStorage.setItem(k, v);
}

/**
 * Se a versão do app e a constante `FORCE_CACHE_RESET_AT_VERSION` exigirem, apaga IndexedDB
 * (dados + imagens), zera `localStorage` exceto entradas do carrinho, limpa `sessionStorage`,
 * desregistra o service worker e retorna `true` para o caller dar um `reload` antes do primeiro paint.
 */
export async function maybeApplyForcedCacheReset(): Promise<boolean> {
  const target = FORCE_CACHE_RESET_AT_VERSION.trim();
  if (!target) return false;

  const current = (pacote.version || '').trim();
  if (!current || compareSemver(current, target) < 0) return false;

  const last = localStorage.getItem(MARKER_KEY);
  if (last === target) return false;

  const cartSnapshot = snapshotCartRawEntries();
  const appTablesBackup = await readAllAppTablesForBackup();

  // SW e Cache API primeiro: evita bundle antigo/cache competindo com IDB novo no PWA.
  try {
    await unregisterServiceWorker({
      preserveLocalStorage: true,
      setReloadFlag: false,
    });
  } catch (e) {
    console.warn('[forceCacheReset] Aviso ao desregistrar SW (seguindo mesmo assim):', e);
  }

  await new Promise<void>((r) => setTimeout(r, 250));

  let idbOk = await deleteAppIndexedDatabasesCompletely();
  if (!idbOk) {
    console.warn('[forceCacheReset] Primeira exclusão do IDB incompleta; nova tentativa em 1,5s…');
    await new Promise<void>((r) => setTimeout(r, 1500));
    idbOk = await deleteAppIndexedDatabasesCompletely();
  }
  if (!idbOk) {
    console.error('[forceCacheReset] IndexedDB não foi limpo com segurança; marcador NÃO gravado — tentará de novo no próximo carregamento.');
    return false;
  }

  try {
    localStorage.clear();
    restoreRawEntries(cartSnapshot);
    restoreAppTablesToLegacy(appTablesBackup);
    localStorage.setItem(MARKER_KEY, target);

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
  } catch (e) {
    console.error('[forceCacheReset] Falha ao limpar/restaurar localStorage:', e);
    return false;
  }

  return true;
}
